import React, { useEffect, useState } from "react";
import { ReactComponent as LogoIcon } from "../../../../assets/Icons/logo.svg";
import { ReactComponent as Edit1 } from "../../../../assets/Icons/edit1.svg";
import AccountEditAdd from "./EditAcc";
import FetchData from "../../../../hooks/FetchData";
import { message } from "antd";
import { useSelector } from "react-redux";
import styled from "styled-components";

const StyledAccount = styled.div`
  .account-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 28px;
  }

  .account-title {
    font-size: 24px;
    font-weight: 600;
    color: #1f243b;
  }

  .edit-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 8px;
    cursor: pointer;
    flex-shrink: 0;
  }
  .edit-button:hover {
    background: #f5f6f8;
  }

  .field-list {
    display: flex;
    flex-direction: column;
    gap: 28px;
    max-width: 640px;
  }

  .field-row {
    display: flex;
    align-items: center;
    gap: 24px;
  }

  .field-label {
    width: 200px;
    flex-shrink: 0;
    font-size: 15px;
    font-weight: 400;
    color: #68728a;
  }

  .field-value {
    font-size: 15px;
    font-weight: 400;
    color: #1f243b;
    word-break: break-word;
  }

  /* Responsive: stack label above value once there isn't room for both
     side by side, instead of squeezing the value column - the same class
     of bug that caused the original wrapping mess. */
  @media (max-width: 560px) {
    .field-row {
      flex-direction: column;
      align-items: flex-start;
      gap: 4px;
    }
    .field-label {
      width: auto;
      font-size: 13px;
    }
  }
`;

const Accounts = () => {
  const [accountView, setAccountView] = useState("account");
  const [accountData, setAccountData] = useState(null);
  const [accountId, setAccountId] = useState(null);
  const [accountLogoUrl, setAccountLogoUrl] = useState("");
  const userInfo = useSelector((state) => state.authTracking.userInfo);

  const handleGetAccountData = async () => {
    if (!userInfo.clientId) return;
    try {
      const response = await FetchData({
        endPoint: `/settings/getaccount/${userInfo.clientId}`,
      });

      if (response?.statusCode === 200 && response?.response?.length > 0) {
        const accountInfo = response.response[0];
        const formattedData = {
          "Organization Name": accountInfo.organization_account || "",
          Logo: accountInfo.logo ? (
            <img src={accountInfo.logo} alt="Organization logo" style={{ height: 28, maxWidth: 120, objectFit: "contain" }} />
          ) : (
            <LogoIcon className="w-[85px] h-[17px]" />
          ),
          "Time Format": accountInfo.time_format || "",
          "Time Zone": accountInfo.time_zone || "N/A",
          Currency: accountInfo.currency || "N/A",
        };
        setAccountData(formattedData);
        setAccountId(accountInfo.account_id);
        setAccountLogoUrl(accountInfo.logo || "");
      } else {
        message.error("Failed to fetch account details");
      }
    } catch (error) {
      console.error("Error fetching account data:", error);
    }
  };
  useEffect(() => {
    // userInfo.clientId is empty until App.js's own mount effect finishes
    // decoding the token into Redux - on a hard refresh both effects fire
    // around the same time, and this one used to run first, requesting
    // /settings/getaccount/ with no clientId at all (CORS/network error,
    // page stuck blank). Re-running once clientId actually lands fixes it
    // without needing to guess at effect ordering.
    handleGetAccountData();
  }, [userInfo.clientId]);

  return (
    <StyledAccount>
      {accountView === "account" && accountData && (
        <div>
          <div className="account-header">
            <h1 className="account-title">Account</h1>
            <div className="edit-button">
              <Edit1 onClick={() => setAccountView("account-edit")} style={{ cursor: "pointer" }} />
            </div>
          </div>
          <div className="field-list">
            {Object.keys(accountData).map((key) => (
              <div key={key} className="field-row">
                <span className="field-label">{key}</span>
                <span className="field-value">{accountData[key]}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {accountView === "account-edit" && accountData && (
        <AccountEditAdd
          setAccountView={setAccountView}
          accountData={accountData}
          accountId={accountId}
          accountLogoUrl={accountLogoUrl}
          onSaved={handleGetAccountData}
        />
      )}
    </StyledAccount>
  );
};

export default Accounts;
